//(components/document-table/DocumentTable.jsx)

import React, { useState, useEffect, useContext, useRef } from "react";
import documentData from "../../api/documents";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import viewDocumentsBulkLink from "../../api/viewDocumentsBulkLink";
import { showSuccess, showError } from "../../utils/toast";
import {
  CaretSortIcon,
  ChevronDownIcon,
  StarIcon,
  StarFilledIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
} from "@radix-ui/react-icons";
import { Button } from "../../shadcn-components/ui/button";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../shadcn-components/ui/tooltip";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../shadcn-components/ui/select";

import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { Checkbox } from "../../shadcn-components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../shadcn-components/ui/dropdown-menu";
import { Input } from "../../shadcn-components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../shadcn-components/ui/table";
import { AuthContext } from "../../context/Auth/AuthContext";
import AddDocument from "./AddDocument";
import Loading from "../material-ui/Loading";
import baseUrl from "../../config/baseUrl";
import "../../styles/DocumentTable.css";
import EditDocumentPopup from "./EditDocumentPopup";
import DeleteDocumentPopup from "./DeleteDocumentPopup";
import markDocumentAsFavourite from "../../api/addFavouriteDocument";
import removeDocumentFromFavourite from "../../api/removeFavouriteDocument";
import DeleteIcon from "@mui/icons-material/Delete";
import markDocumentsAsFavourite from "../../api/addBulkFavouriteDocument";

import getAllFieldTypeList from "../../api/getAllFieldTypeList";
import { fileIcon } from "../common/FileIcons";
import CategoryChip from "../common/CategoryChip";
import DataToolbar from "../common/DataToolbar";
import PaginationSection from "../common/PaginationSection";
import { createStickyColumns } from "../common/stickyColumns";
import TableSkeleton from "./TableSkeleton";
import BulkDeletePopup from "./BulkDeletePopup";
import { BsFillCloudArrowUpFill } from "react-icons/bs";
import { TbCircleCheck } from "react-icons/tb";
import { TiDeleteOutline } from "react-icons/ti";
import { useDebounce } from "use-debounce";
import { toast } from "react-toastify";
import { MdError } from "react-icons/md";
import pdfDetails from "../../api/pdfDetails";
import { CheckCircle, CheckCircleOutline } from "@mui/icons-material";
import { useRoleContext } from "../../context/RolesContext";
import { convertToUTC, formatDate } from "../../utils/formatDate";
import { format } from "date-fns";
import AssignUserPopup from "../email-case-list/case-list-table/AssignUserPopup";
import UnassignUserPopup from "../email-case-list/case-list-table/UnassignUserPopup";

const DocumentTable = ({
  tableRef,
  addDocumentTab,
  showAddDocumentForm,
  onAddDocumentButtonClick,
  clientID,
  checkDocumentOpen,
  assignUserId,
  refetchTrigger,
}) => {
  const savedRoleId = localStorage.getItem("role_id");
  const [loading, setLoading] = useState(true);
  const [allFavourite, setAllFavourite] = useState(false);
  const [documentResponse, setDocumentResponse] = useState([]);

  const { permissions } = useRoleContext();
  const { token, id, is_admin } = useContext(AuthContext);

  const [documentAdded, setDocumentAdded] = useState(0);
  const [documentDeleted, setDocumentDeleted] = useState(0);
  const [favouriteDocuments, setFavouriteDocuments] = useState(false);

  const [bulkAssign, setBulkAssign] = useState(false);
  const [bulkUnassign, setBulkUnassign] = useState(false);
  const [bulkFavouriteDocument, setBulkFavouriteDocument] = useState(false);
  const [bulkDeleteDocument, setBulkDeleteDocument] = useState(false);
  const [bulkDeleteDocIds, setBulkDeleteDocIds] = useState();
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkDownloadProgress, setBulkDownloadProgress] = useState({
    completed: 0,
    total: 0,
  });

  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({
    email_subject: false,
  });
  const [rowSelection, setRowSelection] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedGlobalFilter] = useDebounce(globalFilter, 1000);
  const [selectAllChecked, setSelectAllChecked] = useState();
  const [showFilterButton, setShowFilterButton] = useState(false);
  const [createdAt, setCreatedAt] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [bulkAssignIds, setBulkAssignIds] = useState(null);

  // Stores a JSON snapshot of filter-related values from the previous render.
  // null means the effect hasn't run yet (first mount).
  const prevFiltersRef = useRef(null);

  const hasPermission = (permission) => {
    if (is_admin === "1") return true;
    return permissions?.includes(permission);
  };

  const canCreate = hasPermission("create_all_documents");
  const canEdit = hasPermission("edit_all_documents");
  const canDelete = hasPermission("delete_all_documents");
  const canShare = hasPermission("share_all_documents");
  const canDownload = hasPermission("download_all_documents");
  const canAssign = hasPermission("assign_user");
  const canUnassign = hasPermission("unassign_user");
  const shouldShowActionColumn = canEdit || canDelete;

  const assignUserColumn = {
    accessorKey: "assign_user",
    displayName: "Assigned To",
    filterable: true,
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="p-0"
      >
        Assigned To
        <CaretSortIcon className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="text-left">{row?.getValue("assign_user")}</div>
    ),
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
  };

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <div className="flex items-center">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
          {(table.getIsSomePageRowsSelected() ||
            table.getIsAllPageRowsSelected()) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="p-0 !border-none !shadow-none bg-transparent"
                >
                  <ChevronDownIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => handleBulkAction("download")}
                  disabled={!canDownload || bulkDownloading}
                >
                  {bulkDownloading ? "Downloading..." : "Download"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => handleBulkAction("delete")}
                  disabled={!canDelete}
                >
                  Delete
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => handleBulkAction("favourite")}
                >
                  Mark Favourite
                </DropdownMenuItem>
                {clientID === "null" && (
                  <>
                    <DropdownMenuItem
                      onSelect={() => handleBulkAction("assign")}
                      disabled={!canAssign}
                    >
                      Assign
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => handleBulkAction("unassign")}
                      disabled={!canUnassign}
                    >
                      Unassign
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => {
            setSelectAllChecked(true);
            row.toggleSelected(!!value);
          }}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "favourite",
      displayName: "Favourite",
      header: () => <StarIcon />,
      // favourite arrives as 1 (number) or "1" (string) depending on the
      // API/DB column type - compare as a string so favourited documents
      // always show the filled yellow star.
      cell: ({ row }) =>
        String(row.getValue("favourite")) === "1" ? (
          <StarFilledIcon
            className="text-yellow-500 cursor-pointer"
            onClick={() => handleFavourites(row.original)}
          />
        ) : (
          <StarIcon
            className="cursor-pointer"
            onClick={() => handleFavourites(row.original)}
          />
        ),
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      accessorKey: "document_type",
      displayName: "Type",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Type
          <CaretSortIcon className="ml-2 h-6" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="w-full flex items-center">
          {fileIcon[row.getValue("document_type").toLowerCase()]}
        </div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "document_name",
      displayName: "Name",
      filterable: false,
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Name
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div
          className="wrap-text limited-width capitalize text-left underline cursor-pointer text-blue-500"
          onClick={() => {
            addDocumentTab(row.original);
            if (tableRef.current) {
              tableRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }}
        >
          {row.getValue("document_name")}
        </div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
      enableHiding: false,
    },
    {
      accessorKey: "phone_number",
      displayName: "Phone No",
      filterable: false,
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Phone No
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <span>{row.getValue("phone_number")}</span>,
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
      enableHiding: false,
    },
    {
      accessorKey: "client_name",
      displayName: "Client Name",
      filterable: false,
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Client Name
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const clientName = row.getValue("client_name");
        // Only the Client Unknown Documents page (clientID === "client
        // unknown") falls back to "Unknown" for a missing/empty client_name
        // — everywhere else (All/Unprocessed/Processed Documents) keeps
        // rendering a blank cell exactly as before, driven solely by the
        // client_unknown flag.
        const clientUnknown =
          row.original.client_unknown === "1" ||
          (clientID === "client unknown" && !clientName);
        const clientKnown = row.original.client_unknown === "0" && !!clientName;
        return (
          <div className="flex items-center gap-0.5">
            <div className="mr-2">
              {clientUnknown ? (
                <TbCircleCheck color="#eab308" className="h-6 w-6" />
              ) : clientName || clientKnown ? (
                <TbCircleCheck color="#3b82f6" className="h-6 w-6" />
              ) : null}
            </div>
            <span className="wrap-text capitalize">
              {clientUnknown ? "Unknown" : clientName}
            </span>
          </div>
        );
      },
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "document_source_details",
      displayName: "Source Details",
      filterable: false,
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Source Details
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <span>{row.getValue("document_source_details")}</span>,
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "email_subject",
      displayName: "Subject",
      filterable: false,
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Subject
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <span>{row.getValue("email_subject")}</span>,
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "document_request_setting_name",
      displayName: "Linked Request",
      filterable: false,
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Linked Request
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        let document_request_name = row.getValue(
          "document_request_setting_name",
        );
        const additional_doc_name = row.original.additional_doc_name;

        const capitalizeFirstLetter = (string) =>
          string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();

        if (
          document_request_name === "Additional Document" &&
          additional_doc_name
        ) {
          document_request_name = `${document_request_name} - ${capitalizeFirstLetter(additional_doc_name)}`;
        }

        return (
          <div className="wrap-text flex text-left items-center justify-start">
            <span className="flex items-center justify-center case-doc-tables mr-2">
              {row.original.document_request_status ? (
                row.original.document_request_status?.toLowerCase() ===
                "requested" ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="request-doc-request-icon request-doc-icon">
                          <BsFillCloudArrowUpFill />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>Requested</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : row.original.document_request_status.toLowerCase() ===
                  "uploaded" ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="request-doc-upload-icon request-doc-icon">
                          <BsFillCloudArrowUpFill />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>Uploaded</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : row.original.document_request_status.toLowerCase() ===
                  "approved" ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="request-doc-approved-icon request-doc-icon">
                          <TbCircleCheck />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>Approved</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : row.original.document_request_status.toLowerCase() ===
                  "rejected" ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="request-doc-rejected-icon request-doc-icon">
                          <TiDeleteOutline />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>Rejected</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : null
              ) : null}
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-gray-800 font-medium">
                    {document_request_name && document_request_name.length > 40
                      ? `${document_request_name.substring(0, 40)}...`
                      : document_request_name || ""}
                  </span>
                </TooltipTrigger>
                {document_request_name && document_request_name.length > 40 && (
                  <TooltipContent className="bg-gray-600 max-w-xs">
                    <p>{document_request_name}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    ...(clientID === "null" || clientID === "not null"
      ? [assignUserColumn]
      : []),
    {
      accessorKey: "doc_category",
      displayName: "category",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Category
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger style={{ cursor: "default" }}>
              <CategoryChip
                label={row.getValue("doc_category")}
                color={row.original.bg_color}
              />
            </TooltipTrigger>
            <TooltipContent className="bg-gray-600">
              <p>{row.original.category_desc}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "document_subtype",
      displayName: "Sub Category",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Sub Category
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="wrap-text capitalize">
          {row.getValue("document_subtype")}
        </div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "created_at",
      displayName: "Created At",
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Created At
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize text-wrap">
          {formatDate(row.getValue("created_at"))}
        </div>
      ),
    },
    {
      accessorKey: "updated_at",
      displayName: "Modified At",
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Modified At
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize text-wrap">
          {formatDate(row.getValue("updated_at"))}
        </div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "modified_by_user",
      displayName: "Modified By",
      filterable: false,
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Modified By
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="wrap-text capitalize">
          {row.getValue("modified_by_user")}
        </div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      id: "actions",
      displayName: "Actions",
      enableHiding: false,
      header: () => {
        if (!shouldShowActionColumn) return null;
        return (
          <Button variant="ghost" size="sm" className="p-0 !cursor-default">
            Actions
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="flex gap-2">
          {canEdit && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <EditDocumentPopup
                    setDocumentUpdatedToTrue={setDocumentAddedToTrue}
                    tableValues={row.original}
                  />
                </TooltipTrigger>
                <TooltipContent className="bg-gray-600">
                  <p>Edit Document</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {canDelete && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <DeleteDocumentPopup
                    data={row.original}
                    setDocumentDeletedToTrue={setDocumentDeletedToTrue}
                    setRowSelection={setRowSelection}
                    checkDocumentOpen={checkDocumentOpen}
                  />
                </TooltipTrigger>
                <TooltipContent className="bg-gray-600">
                  <p>Delete Document</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
    },
  ];

  // Pins the leading "select"/"favourite"/"Type" columns to the left edge and
  // the trailing "actions" column to the right edge, so only the data-heavy
  // middle columns scroll horizontally. Widths are fixed so the header and
  // body cells for a given column line up at the same left/right offset.
  const { getStickyCellProps } = createStickyColumns({
    left: { select: 48, favourite: 44, document_type: 70 },
    right: ["actions"],
  });

  // ─── Fetch function — page passed explicitly to avoid stale closure ───────
  const documentResponseFunc = async (page) => {
    setLoading(true);
    try {
      const offset = page - 1;

      const filterParams = {};
      columnFilters.forEach((filterItem) => {
        filterParams[filterItem.id] = filterItem.value;
      });

      const sortParam = sorting
        .map((sortItem) => `${sortItem.id}=${sortItem.desc ? "desc" : "asc"}`)
        .join(",");

      const params = {
        isAdmin: is_admin,
        userId: id,
        assignUserId: assignUserId,
        // Non-admins must not send roleId — the backend applies role-based
        // scoping against it, which was excluding documents (like ones just
        // marked Client Unknown) that don't yet fall under the current
        // role's normal assignment scope. Admins keep the existing behavior.
        roleId: is_admin === "1" ? savedRoleId : null,
        limit: pageSize,
        offset: offset,
        ...filterParams,
        global_search: debouncedGlobalFilter,
        sort: sortParam,
        ...(createdAt ? { created_at: convertToUTC(createdAt) } : {}),
        ...(updatedAt ? { updated_at: convertToUTC(updatedAt) } : {}),
        ...(clientID ? { client_id: clientID } : {}),
      };

      const queryString = new URLSearchParams(params).toString();
      const url = `${baseUrl}/api/get-all-doc-data?${queryString}`;

      const response = await documentData({ token, api: url });

      const documents = response?.data[0]?.document || [];

      setDocumentResponse(documents.length === 0 ? [] : documents);
      setTotalPages(Math.ceil(response?.data[0]?.alldocumentcount / pageSize));
      setAllFavourite(response?.data[0]?.allStarValue === "1");
    } catch (error) {
      setTotalPages(1);
      console.error("Error fetching documents:", error);
      setDocumentResponse([]);
    } finally {
      setLoading(false);
    }
  };

  // ─── Single unified effect ────────────────────────────────────────────────
  //
  // How it works:
  //   - prevFiltersRef stores a JSON snapshot of all filter values from the
  //     last time the effect ran.
  //   - On first mount (prevFiltersRef is null): fetch page 1, save snapshot.
  //   - On subsequent runs: compare current filters to saved snapshot.
  //       • Filters changed → fetch page 1, update currentPage UI, save snapshot.
  //       • Filters unchanged → a non-filter dep changed (page nav or CRUD),
  //         so fetch with currentPage as-is, save snapshot.
  //
  //   Crucially, when filters change we call setCurrentPage(1) AFTER fetching,
  //   not before. This means the state update schedules one more render, but
  //   on that render the filter snapshot will be identical (filters didn't
  //   change again), so the effect will fall into the "filters unchanged" branch
  //   and fetch currentPage — which is now 1. That is one extra call only when
  //   the user was on page > 1 and then changed a filter. If the user was
  //   already on page 1, React bails out of the re-render entirely because
  //   setCurrentPage(1) is a no-op, so zero extra calls in the common case.
  //
  // ─── Main effect: Handle filter/sort/search/page changes ───────────────────
  useEffect(() => {
    const currentFiltersSnapshot = JSON.stringify({
      columnFilters,
      debouncedGlobalFilter,
      pageSize,
      sorting,
      createdAt,
      updatedAt,
      clientID,
    });

    const isFirstMount = prevFiltersRef.current === null;
    const filtersChanged =
      !isFirstMount && prevFiltersRef.current !== currentFiltersSnapshot;

    prevFiltersRef.current = currentFiltersSnapshot;

    if (filtersChanged) {
      documentResponseFunc(1);
      setCurrentPage(1);
    } else {
      documentResponseFunc(currentPage);
    }

    setSelectAllChecked(false);
  }, [
    documentAdded,
    documentDeleted,
    favouriteDocuments,
    bulkFavouriteDocument,
    bulkDeleteDocument,
    currentPage,
    columnFilters,
    debouncedGlobalFilter,
    pageSize,
    sorting,
    createdAt,
    updatedAt,
    clientID,
  ]);

  const isFirstRender = useRef(true);

  // ✅ Handle refetch trigger — skip first mount
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    documentResponseFunc(currentPage);
  }, [refetchTrigger]);
  // ─────────────────────────────────────────────────────────────────────────

  const setDocumentAddedToTrue = () => {
    setDocumentAdded((count) => count + 1);
  };

  const setDocumentDeletedToTrue = () => {
    setDocumentDeleted((count) => count + 1);
  };

  const handleFavourites = async (row) => {
    const { document_id, favourite } = row;
    if (favourite !== "1") {
      await markDocumentAsFavourite({ token, ids: document_id });
      setFavouriteDocuments((prev) => !prev);
    } else {
      await removeDocumentFromFavourite({ token, ids: document_id });
      setFavouriteDocuments((prev) => !prev);
    }
  };

  const handleBulkAction = async (actionType) => {
    const selectedRowsData = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original);
    const ids = selectedRowsData.map((row) => row.document_id).join(",");
    if (actionType === "delete") {
      setBulkDeleteDocIds(ids);
      setBulkDeleteDocument(true);
    } else if (actionType === "assign") {
      setBulkAssign(true);
      setBulkAssignIds(ids);
    } else if (actionType === "unassign") {
      setBulkUnassign(true);
      setBulkAssignIds(ids);
    } else if (actionType === "download") {
      handleBulkDownload(selectedRowsData);
    } else {
      setBulkFavouriteDocument(true);
      await markDocumentsAsFavourite({ token, ids });
    }
  };

  // Extracts the file extension from a URL, ignoring query params
  const getFileExtensionFromUrl = (url) => {
    try {
      const pathname = new URL(url).pathname;
      const match = pathname.match(/\.([a-zA-Z0-9]+)$/);
      return match ? match[1].toLowerCase() : "";
    } catch {
      const match = url?.split("?")[0]?.match(/\.([a-zA-Z0-9]+)$/);
      return match ? match[1].toLowerCase() : "";
    }
  };

  const handleBulkDownload = async (selectedRowsData) => {
    if (!selectedRowsData.length) return;

    setBulkDownloading(true);
    setBulkDownloadProgress({ completed: 0, total: selectedRowsData.length });

    const zip = new JSZip();
    const usedNames = new Set();

    try {
      const docIds = selectedRowsData.map((row) => row.document_id);

      const bulkResponse = await viewDocumentsBulkLink({ token, docIds });
      const links = bulkResponse?.data || [];

      if (!links.length) {
        showError(
          "Could not retrieve download links for the selected documents",
        );
        return;
      }

      const rowById = Object.fromEntries(
        selectedRowsData.map((row) => [row.document_id, row]),
      );

      await Promise.all(
        links.map(async (linkInfo) => {
          try {
            const { document_id, pdflink } = linkInfo;
            if (!pdflink) {
              console.error(`No download link for document ${document_id}`);
              return;
            }

            const fileResponse = await fetch(
              `${baseUrl}/api/download-document-proxy?document_id=${document_id}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (!fileResponse.ok) {
              console.error(`Failed to fetch document ${document_id}`);
              return;
            }
            const blob = await fileResponse.blob();

            let fileName =
              rowById[document_id]?.document_name ||
              linkInfo.document_file_name ||
              `document-${document_id}`;

            const extension = getFileExtensionFromUrl(pdflink);
            if (
              extension &&
              !fileName.toLowerCase().endsWith(`.${extension}`)
            ) {
              fileName = `${fileName}.${extension}`;
            }

            if (usedNames.has(fileName)) {
              const dotIndex = fileName.lastIndexOf(".");
              const base =
                dotIndex !== -1 ? fileName.slice(0, dotIndex) : fileName;
              const ext = dotIndex !== -1 ? fileName.slice(dotIndex) : "";
              fileName = `${base}-${document_id}${ext}`;
            }
            usedNames.add(fileName);

            zip.file(fileName, blob);
          } catch (err) {
            console.error(
              `Error processing document ${linkInfo.document_id}:`,
              err,
            );
          } finally {
            setBulkDownloadProgress((prev) => ({
              ...prev,
              completed: prev.completed + 1,
            }));
          }
        }),
      );

      if (Object.keys(zip.files).length === 0) {
        showError("Could not download any of the selected documents");
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `documents-${Date.now()}.zip`);
      showSuccess("Documents downloaded successfully");
    } catch (err) {
      showError("Failed to download selected documents");
    } finally {
      setBulkDownloading(false);
      table.resetRowSelection();
      setBulkDownloadProgress({ completed: 0, total: 0 });
    }
  };

  const table = useReactTable({
    data: documentResponse,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    initialState: { pageSize: 20 },
    manualPagination: true,
    manualSorting: true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pageSize,
      globalFilter,
    },
    onGlobalFilterChange: (filterValue) => {
      setGlobalFilter(filterValue);
    },
  });

  return (
    <>
      {bulkDeleteDocument ? (
        <BulkDeletePopup
          setDocumentDeletedToTrue={setDocumentDeletedToTrue}
          Docbulkid={bulkDeleteDocIds}
          bulkDeleteDocument={bulkDeleteDocument}
          setBulkDeleteDocument={setBulkDeleteDocument}
          setRowSelection={setRowSelection}
        />
      ) : null}
      {bulkAssign ? (
        <AssignUserPopup
          selectedRows={table
            ?.getFilteredSelectedRowModel()
            ?.rows.map((row) => row.original)}
          setDocumentUpdatedToTrue={setDocumentAddedToTrue}
          bulkAssign={bulkAssign}
          setBulkAssign={setBulkAssign}
          setRowSelection={setRowSelection}
          bulkAssignIds={bulkAssignIds}
        />
      ) : null}
      {bulkUnassign ? (
        <UnassignUserPopup
          selectedRows={table
            ?.getFilteredSelectedRowModel()
            ?.rows.map((row) => row.original)}
          setDocumentUpdatedToTrue={setDocumentAddedToTrue}
          bulkUnassign={bulkUnassign}
          setBulkUnassign={setBulkUnassign}
          setRowSelection={setRowSelection}
          bulkAssignIds={bulkAssignIds}
        />
      ) : null}
      {bulkDownloading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="flex flex-col items-center gap-3 rounded-lg bg-white px-8 py-6 shadow-lg">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
            <p className="text-sm font-medium text-gray-700">
              Preparing your download...
            </p>
          </div>
        </div>
      )}
      <div className="document-table documents-table-page">
        {showAddDocumentForm ? (
          <AddDocument
            onAddDocumentButtonClick={onAddDocumentButtonClick}
            setDocumentAddedToTrue={setDocumentAddedToTrue}
          />
        ) : (
          <div className="documents-content">
            <div className="document__table documents-table-layout">
              <div className="documents-table-surface">
                <div className="documents-toolbar flex justify-between items-center flex-wrap gap-4">
                  <Input
                    placeholder="Search By Document Name..."
                    value={globalFilter}
                    onChange={(event) =>
                      table.setGlobalFilter(event.target.value)
                    }
                    className="max-w-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowFilterButton(!showFilterButton)}
                    >
                      <FilterListIcon sx={{ fontSize: 18 }} className="mr-1" />
                      Filter
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="ml-auto">
                          <ViewColumnIcon sx={{ fontSize: 18 }} className="mr-1" />
                          Columns <ChevronDownIcon className="ml-2 h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {table
                          .getAllColumns()
                          .filter((column) => column.getCanHide())
                          .map((column) => (
                            <DropdownMenuCheckboxItem
                              key={column.id}
                              className="capitalize"
                              checked={column.getIsVisible()}
                              onCheckedChange={(value) =>
                                column.toggleVisibility(!!value)
                              }
                            >
                              {column.columnDef.displayName}
                            </DropdownMenuCheckboxItem>
                          ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                {showFilterButton && (
                  <DataToolbar
                    table={table}
                    apiUrl="/api/get-document-filter-name"
                    createdAt={createdAt}
                    updatedAt={updatedAt}
                    setCreatedAt={setCreatedAt}
                    setUpdatedAt={setUpdatedAt}
                  />
                )}
                <div className="documents-table-frame rounded-md border">
                  {loading ? (
                    <TableSkeleton />
                  ) : (
                      <Table className="w-full table-auto">
                        <TableHeader className="static">
                          {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                              {headerGroup.headers.map((header) => {
                                const sticky = getStickyCellProps(
                                  header.column.id,
                                  true,
                                );
                                return (
                                  <TableHead
                                    key={header.id}
                                    style={sticky.style}
                                  >
                                    {header.isPlaceholder
                                      ? null
                                      : flexRender(
                                          header.column.columnDef.header,
                                          header.getContext(),
                                        )}
                                  </TableHead>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableHeader>
                        <TableBody>
                          {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                              <TableRow
                                key={row.id}
                                data-state={row.getIsSelected() && "selected"}
                              >
                                {row.getVisibleCells().map((cell) => {
                                  const sticky = getStickyCellProps(
                                    cell.column.id,
                                  );
                                  return (
                                    <TableCell
                                      key={cell.id}
                                      className="py-1 px-2 text-xs truncate"
                                      style={sticky.style}
                                    >
                                      {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext(),
                                      )}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={columns.length}
                                className="h-24 text-center"
                              >
                                {loading ? (
                                  <TableSkeleton />
                                ) : (
                                  "No records found"
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                  )}
                </div>
                <div className="documents-pagination flex items-center justify-between space-x-2 flex-wrap gap-4">
                  <div className="flex items-center gap-5">
                    <div className="flex-1 text-sm text-muted-foreground">
                      {table.getFilteredSelectedRowModel().rows.length} of{" "}
                      {table.getFilteredRowModel().rows.length} row(s) selected.
                    </div>
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium">Rows per page</p>
                      <Select
                        value={`${table.getState().pageSize}`}
                        onValueChange={(value) => {
                          setPageSize(Number(value));
                          setCurrentPage(1);
                        }}
                      >
                        <SelectTrigger className="w-auto">
                          <SelectValue
                            placeholder={table.getState().pageSize}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {[10, 20, 30, 40, 50].map((pageSize) => (
                              <SelectItem key={pageSize} value={`${pageSize}`}>
                                {pageSize}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-x-2">
                    <PaginationSection
                      setCurrentPage={setCurrentPage}
                      totalPages={totalPages}
                      currentPage={currentPage}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DocumentTable;
